import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { baseUrl } from 'api/config'
import axios from 'axios'

export interface ServiceMenuItem {
  PK: string
  SK: string
  id: string
  products_uuid: string
  title: string
  sku: string
  service_line: string
  category_name: string
  subcategory: string
  points: string
  price: string
  cost: string
  margin: string
  description: string
  estimated_hours: string
  delivery_role: string
  ta_type: string
  ta_category: string
  ai_enhanced: boolean
  status: string
  isDeprecated: boolean
  owner: string
  qty: string
  variant?: string
  createdAt?: string
  updatedAt?: string
}

const SERVICE_MENU_KEY = ['service-menu']

const fetchServiceMenu = async (): Promise<ServiceMenuItem[]> => {
  const { data } = await axios.get<ServiceMenuItem[]>(`${baseUrl}/aws/service-menu`)
  return data
}

export const useServiceMenu = () => {
  return useQuery({
    queryKey: SERVICE_MENU_KEY,
    queryFn: fetchServiceMenu,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 30,
  })
}

export const useCreateServiceMenuItem = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (item: Partial<ServiceMenuItem>) =>
      axios.post<ServiceMenuItem>(`${baseUrl}/aws/service-menu`, item).then((r) => r.data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: SERVICE_MENU_KEY }),
  })
}

export const useUpdateServiceMenuItem = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ pk, sk, ...fields }: { pk: string; sk: string } & Partial<ServiceMenuItem>) =>
      axios
        .put<ServiceMenuItem>(`${baseUrl}/aws/service-menu/${encodeURIComponent(pk)}/${encodeURIComponent(sk)}`, fields)
        .then((r) => r.data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: SERVICE_MENU_KEY }),
  })
}

export const useDeleteServiceMenuItem = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ pk, sk }: { pk: string; sk: string }) =>
      axios.delete(`${baseUrl}/aws/service-menu/${encodeURIComponent(pk)}/${encodeURIComponent(sk)}`).then((r) => r.data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: SERVICE_MENU_KEY }),
  })
}

export const useDeprecateServiceMenuItem = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ pk, sk, isDeprecated }: { pk: string; sk: string; isDeprecated: boolean }) =>
      axios
        .patch(`${baseUrl}/aws/service-menu/${encodeURIComponent(pk)}/${encodeURIComponent(sk)}/deprecate`, {
          isDeprecated,
        })
        .then((r) => r.data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: SERVICE_MENU_KEY }),
  })
}
